import { Button } from '@app/components/various/Button';
import { ChevronLeft, ChevronRight } from '@app/components/various/Icons';
import { useDebouncedCallback, useScreenResize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings/@centra';
import React from 'react';

interface Props {
  title: string;
  skeletonComponent: React.ReactNode;
  skeletonRepeat?: number;
  isLoading: boolean;
  products: Product.Full[];
  renderItem: (item: Product.Full) => React.ReactNode;
}

export const ProductCarousel = ({ title, skeletonComponent, isLoading, products, skeletonRepeat = 3, renderItem }: Props) => {
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const [isPrevButtonDisabled, setIsPrevButtonDisabled] = React.useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = React.useState(false);
  const [shouldHideButtons, setShouldHideButtons] = React.useState(true);
  const productsCount = products.length;

  const calculateButtonsVisibility = React.useCallback(() => {
    if (!isDefined(listRef.current)) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = listRef.current;

    setIsPrevButtonDisabled(scrollLeft < 1);
    setIsNextButtonDisabled(scrollLeft + clientWidth + 1 >= scrollWidth);
    setShouldHideButtons(scrollWidth <= clientWidth);
  }, []);

  React.useEffect(() => {
    if (productsCount === 0) {
      return;
    }

    calculateButtonsVisibility();
  }, [calculateButtonsVisibility, productsCount]);

  useScreenResize(useDebouncedCallback(calculateButtonsVisibility));

  const handleButtonClick = (direction: 'next' | 'prev') => {
    if (!isDefined(listRef.current)) {
      return;
    }

    const offset = listRef.current.scrollWidth / products.length;

    listRef.current.scrollBy({
      behavior: 'smooth',
      left: direction === 'next' ? offset : -offset,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <h3 className="heading-3">{title}</h3>
        <div className="-mx-4 flex overflow-hidden pr-4">
          {[...Array.from({ length: skeletonRepeat })].map((_, index) => (
            <div key={index} className="w-1/2 shrink-0 snap-start pl-4 xl:w-1/3 2xl:w-1/4">
              {skeletonComponent}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="heading-3">{title}</h3>
      <div className="relative">
        <ul
          ref={listRef}
          className="-mx-4 flex snap-x snap-mandatory overflow-x-auto pr-4 no-scrollbar"
          onScroll={calculateButtonsVisibility}
        >
          {products.map(product => (
            <li
              key={`${product.product}${product.variant}`}
              className="w-1/2 shrink-0 snap-start pl-4 sm:w-1/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4"
            >
              {renderItem(product)}
            </li>
          ))}
        </ul>
        {!shouldHideButtons && (
          <>
            <Button
              icon={ChevronLeft}
              variant="bordered"
              color="secondary"
              tabIndex={-1}
              disabled={isPrevButtonDisabled}
              className="absolute left-0 top-1/2 z-elevated -translate-x-4 -translate-y-1/2 bg-white lg:-translate-x-1/2"
              onClick={() => handleButtonClick('prev')}
            />
            <Button
              icon={ChevronRight}
              variant="bordered"
              color="secondary"
              tabIndex={-1}
              disabled={isNextButtonDisabled}
              className="absolute right-0 top-1/2 z-elevated -translate-y-1/2 translate-x-4 bg-white lg:translate-x-1/2"
              onClick={() => handleButtonClick('next')}
            />
          </>
        )}
      </div>
    </div>
  );
};
